import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { ExcelTable } from "@/components/ExcelTable";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Gauge, AlertTriangle, Fuel } from "lucide-react";
import { LineChart, Line, ResponsiveContainer, YAxis, Tooltip } from "recharts";

const fmtNum = (n) => new Intl.NumberFormat("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
const fmtDate = (s) => { try { return new Date(s).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" }); } catch { return ""; } };
const unit = (t) => (t === "ore" ? "ore/l" : "km/l");
// Soglia di peggioramento configurabile (Impostazioni): consumo sotto (1 - soglia) del previsto => badge rosso

export default function Consumi() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [thr, setThr] = useState(0.15);
  const [sel, setSel] = useState(null);
  const [refuels, setRefuels] = useState(null);

  const openVehicle = async (row) => {
    setSel({ vehicle_id: row.vehicle_id, vehicle_name: row.vehicle_name, vehicle_plate: row.vehicle_plate, consumption_type: row.consumption_type });
    setRefuels(null);
    try {
      const r = await api.get(`/fuel/consumption/${row.vehicle_id}/refuels`);
      setRefuels(r.data.refuels.map((x) => ({ ...x, date_str: fmtDate(x.date) })));
    } catch {
      setRefuels([]);
    }
  };

  const isWorse = (value, expected) => expected > 0 && value != null && value < expected * (1 - thr);

  useEffect(() => {
    api.get("/fuel/consumption").then((r) => setItems(r.data)).finally(() => setLoading(false));
    api.get("/settings").then((r) => setThr(((r.data.consumption_alert_pct ?? 15) || 15) / 100)).catch(() => {});
  }, []);

  const consCell = (value, type, expected) => {
    if (value == null) return <span className="text-slate-300">—</span>;
    let cls = "text-slate-900";
    if (expected > 0) cls = value >= expected ? "text-emerald-700" : isWorse(value, expected) ? "text-rose-600" : "text-amber-600";
    return <span className={`font-heading font-semibold ${cls}`}>{fmtNum(value)} {unit(type)}</span>;
  };

  const rows = useMemo(() => items.map((r) => ({
    ...r,
    last_cons: r.last ? r.last.consumption : null,
    alert: isWorse(r.trend ?? r.last?.consumption ?? null, r.avg_consumption) ? "Consumo elevato" : "Regolare",
    andamento: "",
  })), [items, thr]);

  const columns = [
    { key: "vehicle_name", header: "Mezzo", sortValue: (r) => r.vehicle_name, className: "font-medium text-slate-900", cell: (r) => (
        <>{r.vehicle_name}{r.vehicle_plate && <span className="block text-xs text-slate-400">{r.vehicle_plate}</span>}</>
      ) },
    { key: "alert", header: "Stato", cell: (r) => (
        r.alert === "Consumo elevato" ? (
          <span data-testid={`consumo-alert-${r.vehicle_id}`} className="inline-flex items-center gap-1 rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-[11px] font-semibold text-rose-600">
            <AlertTriangle className="h-3 w-3" /> Consumo elevato
          </span>
        ) : (
          <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-medium text-slate-500">Regolare</span>
        )
      ) },
    { key: "avg_consumption", header: "Consumo previsto", type: "number", align: "right", className: "text-right text-slate-600", cell: (r) => r.avg_consumption ? `${fmtNum(r.avg_consumption)} ${unit(r.consumption_type)}` : <span className="text-slate-300">—</span> },
    { key: "last_cons", header: "Ultimo prelievo", type: "number", align: "right", sortValue: (r) => (r.last_cons == null ? -1 : r.last_cons), className: "text-right", cell: (r) => (
        <div className="text-right">
          {consCell(r.last?.consumption ?? null, r.consumption_type, r.avg_consumption)}
          {r.last && <div className="text-xs font-normal text-slate-400">{fmtNum(r.last.distance)} {r.consumption_type === "ore" ? "ore" : "km"} · {fmtNum(r.last.liters)} l</div>}
        </div>
      ) },
    { key: "trend", header: "Trend consumi", type: "number", align: "right", sortValue: (r) => (r.trend == null ? -1 : r.trend), className: "text-right", cell: (r) => consCell(r.trend ?? null, r.consumption_type, r.avg_consumption) },
    { key: "andamento", header: "Andamento", align: "right", className: "text-right", cell: (r) => {
        const data = r.series || [];
        if (data.length < 2) return <span className="text-slate-300">—</span>;
        const stroke = isWorse(r.trend ?? null, r.avg_consumption) ? "#e11d48" : "#002FA7";
        return (
          <div className="ml-auto h-10 w-32" data-testid={`consumo-chart-${r.vehicle_id}`}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data} margin={{ top: 4, right: 2, bottom: 2, left: 2 }}>
                <YAxis hide domain={["dataMin", "dataMax"]} />
                <Tooltip cursor={false} contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0", padding: "4px 8px" }} formatter={(v) => [`${fmtNum(v)} ${unit(r.consumption_type)}`, "Consumo"]} />
                {r.avg_consumption > 0 && <Line type="monotone" dataKey={() => r.avg_consumption} stroke="#94a3b8" strokeDasharray="3 3" strokeWidth={1} dot={false} isAnimationActive={false} />}
                <Line type="monotone" dataKey="consumption" stroke={stroke} strokeWidth={2} dot={{ r: 2 }} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        );
      } },
    { key: "refuels", header: "Rifornimenti", type: "number", align: "right", className: "text-right text-slate-500", cell: (r) => r.refuels },
  ];

  return (
    <div>
      <div className="mb-4">
        <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">Consumi</h1>
        <p className="mt-1 text-sm text-slate-500">
          Consumo medio previsto (anagrafica mezzo) a confronto con quello risultante dai rifornimenti. Ordina e filtra ogni colonna dall'intestazione, come in Excel. Un badge rosso segnala i mezzi con consumo peggiore di oltre il {Math.round(thr * 100)}% rispetto al previsto.
        </p>
      </div>
      <Card className="rounded-lg border-slate-200 shadow-sm">
        {loading ? (
          <p className="p-6 text-slate-400">Caricamento...</p>
        ) : (
          <div className="p-4">
            <ExcelTable data={rows} testid="consumi-table" onRowClick={openVehicle} rowTestId={(r) => `consumi-row-${r.vehicle_id}`} initialSort={{ key: "vehicle_name", dir: "asc" }} emptyIcon={Gauge} emptyText="Nessun mezzo" columns={columns} />
          </div>
        )}
      </Card>

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="max-w-3xl" data-testid="consumi-refuels-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Prelievi · {sel?.vehicle_name}</DialogTitle>
            <DialogDescription>{sel?.vehicle_plate ? `${sel.vehicle_plate} · ` : ""}Tutti i rifornimenti approvati su questo mezzo</DialogDescription>
          </DialogHeader>
          <div className="max-h-[65vh] overflow-y-auto pr-1">
            {refuels === null ? (
              <p className="p-6 text-slate-400">Caricamento...</p>
            ) : (
              <ExcelTable data={refuels} testid="consumi-refuels-table" initialSort={{ key: "date_str", dir: "desc" }} emptyIcon={Fuel} emptyText="Nessun prelievo registrato" columns={[
                { key: "date_str", header: "Data", sortValue: (r) => new Date(r.date).getTime(), className: "whitespace-nowrap text-slate-500", cell: (r) => r.date_str },
                { key: "usage_value", header: sel?.consumption_type === "ore" ? "Ore" : "Km", type: "number", align: "right", className: "text-right text-slate-700", cell: (r) => r.usage_value ? `${fmtNum(r.usage_value)} ${r.usage_type === "ore" ? "ore" : "km"}` : <span className="text-slate-300">—</span> },
                { key: "liters", header: "Litri", type: "number", align: "right", className: "text-right text-slate-700", cell: (r) => r.liters ? `${fmtNum(r.liters)} l` : <span className="text-slate-300">—</span> },
                { key: "consumption", header: "Consumo", type: "number", align: "right", className: "text-right font-heading font-semibold text-slate-900", cell: (r) => r.consumption != null ? `${fmtNum(r.consumption)} ${unit(r.usage_type)}` : <span className="text-slate-300">—</span> },
                { key: "amount", header: "Importo", type: "number", align: "right", className: "text-right text-slate-600", cell: (r) => `€ ${fmtNum(r.actual_amount != null ? r.actual_amount : r.amount)}` },
                { key: "employee_name", header: "Da chi", className: "text-slate-700", cell: (r) => (<span>{r.employee_name}{r.source === "flux" ? <span className="ml-2 inline-block rounded-full border border-violet-200 bg-violet-50 px-2 py-0.5 text-[10px] font-medium text-violet-700">Flux</span> : null}</span>) },
              ]} />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
